// ─── batch-70 (FO-530, spec 230) — de sollicitant: één bronlijst en certificaten als vinkmodel ───
//
// Eén bron voor API én backoffice (schema's, DTO's, vaste lijsten). De pure
// regels (varianten herkennen, legacy-tekst omzetten, geldigheid markeren) staan
// in `@streep/utils` (`recruitment-sollicitant.ts`). Wordt via `kandidaten.ts`
// geëxporteerd (geen eigen regel in de gedeelde index).
import { z } from 'zod'

const id = z.string().trim().min(1).max(60)

/** Bestaat deze 'yyyy-MM-dd' als kalenderdag? ('2026-02-31' en '2026-13-01' niet; anders stil 3 maart of een 500.) */
export function isBestaandeKalenderdag(ymd: string): boolean {
  const [j, m, d] = ymd.split('-').map(Number) as [number, number, number]
  const dt = new Date(Date.UTC(j, m - 1, d))
  return dt.getUTCFullYear() === j && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d
}

const datum = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik een datum (jjjj-mm-dd)')
  .refine(isBestaandeKalenderdag, 'Deze datum bestaat niet')

// ─── Bron van de sollicitant (§A) ───────────────────────────────────────────

/**
 * §A1 — standaard-bronlijst (zonder opgeslagen instelling). Beheer op
 * Recruitment > Instellingen; nieuw-formulier, bewerk-sheet en lijstfilter lezen
 * dezelfde lijst (`leesKandidaatBronnen`). Facebook staat erin: daar draaien de
 * advertenties.
 */
export const KANDIDAAT_BRONNEN_STANDAARD: readonly string[] = [
  'Indeed',
  'LinkedIn',
  'Facebook',
  'Website',
  'Eigen netwerk',
  'Werving via medewerker',
  'Spontane sollicitatie',
  'Telefonisch',
  'E-mail',
  'Anders',
]

/** De keuze waarbij de recruiter zelf een bron typt (§A1, §6.1). */
export const KANDIDAAT_BRON_ANDERS = 'Anders'

/** §6.1 — maximale lengte van een bron in de instellingenlijst. */
export const KANDIDAAT_BRON_MAX_TEKENS = 50

/** Vergelijkingssleutel voor uniekheid: hoofdletters en spaties maken niet uit. */
export function bronUniekSleutel(bron: string): string {
  return bron.trim().replace(/\s+/g, ' ').toLocaleLowerCase('nl-NL')
}

/**
 * §6.1 — de bronlijst bij OPSLAAN in de instellingen: elke bron uniek (hoofdletters
 * tellen niet) en maximaal 50 tekens. Bewust alleen op het schrijfpad: de leesweg
 * (`getRecruitmentInstellingen`) blijft ruim, zodat een oude, langere waarde nooit
 * de hele instellingenset laat terugvallen op de standaard.
 */
export const recruitmentBronnenSchema = z
  .array(
    z
      .string()
      .trim()
      .min(1, 'Geef de bron een naam')
      .max(KANDIDAAT_BRON_MAX_TEKENS, `Een bron is maximaal ${KANDIDAAT_BRON_MAX_TEKENS} tekens`),
  )
  .max(50, 'Maximaal 50 bronnen')
  .superRefine((lijst, ctx) => {
    const gezien = new Map<string, number>()
    lijst.forEach((bron, i) => {
      const sleutel = bronUniekSleutel(bron)
      const eerder = gezien.get(sleutel)
      if (eerder !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [i],
          message: `"${bron}" staat al in de lijst (als "${lijst[eerder]}")`,
        })
      } else {
        gezien.set(sleutel, i)
      }
    })
  })

export interface KandidaatBronInGebruik {
  /** De waarde zoals hij op de kandidaten staat (ook oude vrije varianten). */
  bron: string
  aantal: number
}

/** `GET /recruitment/bronnen` — de beheerde lijst plus de waarden die echt voorkomen. */
export interface KandidaatBronnenDTO {
  lijst: string[]
  inGebruik: KandidaatBronInGebruik[]
}

/** §A1 — normalisatie: varianten ("linkedin", "LinkedIn ") samenvoegen tot één bron. */
export const bronSamenvoegenSchema = z.object({
  van: z.array(z.string().min(1).max(100)).min(1, 'Kies minstens één variant').max(20),
  naar: z.string().trim().min(1, 'Kies de bron waarin je samenvoegt').max(KANDIDAAT_BRON_MAX_TEKENS),
})
export type BronSamenvoegenInput = z.infer<typeof bronSamenvoegenSchema>

export interface BronSamenvoegenResultaatDTO {
  /** Aantal kandidaten waarvan de bron is aangepast. */
  aantal: number
}

// ─── Certificaten als vinkmodel (§C) ────────────────────────────────────────

/**
 * §C4 — de certificaten die de recruiter het vaakst aanvinkt, bovenaan de lijst
 * (op catalogus-code). De rest van de catalogus staat eronder, doorzoekbaar.
 * Rijbewijzen zijn geen certificaattype: die blijven de rijbewijs-keuze op de
 * kandidaat (één waarheid, gaat bij aanname naar de medewerker).
 */
export const KANDIDAAT_CERTIFICAAT_VOORKEUR_CODES: readonly string[] = [
  'VCA_BASIS',
  'VCA_VOL',
  'NEN3140_VP',
  'NEN3140_VA',
  'BHV',
  'EHBO',
  'HEFTRUCK',
  'HOOGWERKER_1A',
  'HOOGWERKER_1B',
  'HOOGWERKER_3A',
  'HOOGWERKER_3B',
  'ADR',
]

/** §C5 — binnen zoveel maanden verlopend = oranje markering. */
export const CERTIFICAAT_BIJNA_VERLOPEN_MAANDEN = 3

/** Eén certificaattype uit de bestaande `CertificateType`-catalogus (voor de vinklijst). */
export interface CertificaatCatalogusItemDTO {
  id: string
  naam: string
  code: string
  validityMonths: number | null
}

export const kandidaatCertificaatVinkjeSchema = z.object({
  certificateTypeId: id,
  /** Leeg = onbekend (§6.2: toegestaan, grijs gemarkeerd). */
  geldigTot: datum.nullable().optional(),
})
export type KandidaatCertificaatVinkje = z.infer<typeof kandidaatCertificaatVinkjeSchema>

/** `PUT /kandidaten/:id/certificaten` — de volledige set vinkjes (niet aangevinkt = weg). */
export const kandidaatCertificatenSchema = z
  .object({
    vinkjes: z.array(kandidaatCertificaatVinkjeSchema).max(100),
  })
  .superRefine((v, ctx) => {
    const gezien = new Set<string>()
    v.vinkjes.forEach((vinkje, i) => {
      if (gezien.has(vinkje.certificateTypeId)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['vinkjes', i, 'certificateTypeId'], message: 'Dit certificaat staat er al in' })
      }
      gezien.add(vinkje.certificateTypeId)
    })
  })
export type KandidaatCertificatenInput = z.infer<typeof kandidaatCertificatenSchema>

/**
 * `POST /kandidaten/:id/certificaten/omzetten` — de oude vrije tekst omzetten
 * naar vinkjes. Eerst een voorstel (`toepassen: false`), dan de keuzes van de
 * recruiter (`toepassen: true`): een dubbelzinnige afkorting ("VCA") wordt nooit
 * stil ingevuld. `restWissen` haalt wat niet is omgezet bewust weg (bv. nadat
 * het als opleiding is vastgelegd); de oude tekst staat dan in de audit.
 */
export const certificatenOmzettenSchema = z.discriminatedUnion('toepassen', [
  z.object({ toepassen: z.literal(false) }),
  z
    .object({
      toepassen: z.literal(true),
      keuzes: z.array(z.object({ fragment: z.string().trim().min(1).max(200), certificateTypeId: id })).max(50),
      restWissen: z.boolean().optional(),
    })
    .superRefine((v, ctx) => {
      if (v.keuzes.length === 0 && !v.restWissen) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['keuzes'], message: 'Kies minstens één certificaat om aan te vinken, of haal de rest van de tekst weg' })
      }
    }),
])
export type CertificatenOmzettenInput = z.infer<typeof certificatenOmzettenSchema>

export type CertificaatFragmentUitkomst = 'GEVONDEN' | 'MEERDERE' | 'ONBEKEND'

export interface CertificaatOmzetFragmentDTO {
  /** Het stukje tekst ("VCA", "BHV", "lasdiploma"). */
  fragment: string
  uitkomst: CertificaatFragmentUitkomst
  /** Passende types (bij MEERDERE: de keuzes, op naam gesorteerd). */
  kandidaten: Array<{ certificateTypeId: string; naam: string }>
  /** Voorgestelde keuze (bij MEERDERE de eerste, zichtbaar als keuze). */
  voorstelId: string | null
  /** Het type is al aangevinkt bij de kandidaat. */
  alAangevinkt: boolean
}

export interface CertificaatOmzetVoorstelDTO {
  tekst: string
  fragmenten: CertificaatOmzetFragmentDTO[]
}

export interface CertificaatOmzetResultaatDTO {
  /** Aantal nieuw aangevinkte certificaten. */
  aangevinkt: number
  /** Wat van de oude tekst niet is omgezet (blijft read-only staan), of null. */
  overgebleven: string | null
}
